import React, { useEffect, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import RuleIcon from '@mui/icons-material/Rule';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Box, Button, Chip, Collapse, Divider, Paper, Stack, TextField, Typography } from '@mui/material';

import type { SetupChecklistRow } from '../setupRules';
import type { ManualApprovalState, SetupInputOverrides } from '../types';

import HiddenApprovedFooter from './HiddenApprovedFooter';

interface Props {
  hiddenApprovedCount: number;
  inputOverrides: SetupInputOverrides;
  manualApprovals: ManualApprovalState;
  rows: Array<SetupChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onInputOverrideChange: (rowId: string, evidenceLabel: string, value: string) => void;
}

export default function SetupReportCard({
  hiddenApprovedCount,
  inputOverrides,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
  onInputOverrideChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <Stack spacing={1.5}>
      {rows.length === 0 ? (
        <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
          No setup rows match this view.
        </Paper>
      ) : (
        rows.map((row) => {
          const manuallyApproved = !!manualApprovals[row.id];
          const approved = row.status === 'approved' || manuallyApproved;
          const isExpanded = expandedRows[row.id] ?? true;
          const approvalLabel = getApprovalLabel(approved, manuallyApproved);

          return (
            <Paper
              key={row.id}
              variant="outlined"
              sx={{
                borderColor: approved ? 'success.light' : 'error.light',
                borderRadius: 1.25,
                overflow: 'hidden',
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{
                  bgcolor: approved ? 'success.50' : 'error.50',
                  px: 2.5,
                  py: 2.25,
                }}
              >
                <Box sx={{ color: approved ? 'success.main' : 'error.main', display: 'flex', pt: 0.25 }}>
                  {approved ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {row.field}
                    </Typography>
                    <Chip
                      color={approved ? 'success' : 'error'}
                      icon={approved ? <CheckIcon /> : <ErrorOutlineIcon />}
                      label={approvalLabel}
                      size="small"
                      variant="outlined"
                      sx={{ bgcolor: 'background.paper', fontWeight: 700 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {row.summary}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Button
                    color="inherit"
                    size="small"
                    variant="text"
                    endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    onClick={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
                    sx={{ textTransform: 'none' }}
                  >
                    {isExpanded ? 'Hide details' : 'Show details'}
                  </Button>
                  {approved ? (
                    <Button
                      color="inherit"
                      size="small"
                      variant="text"
                      startIcon={<KeyboardReturnIcon />}
                      disabled
                      sx={{ textTransform: 'none' }}
                    >
                      Require approval
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      size="small"
                      variant="contained"
                      startIcon={<CheckIcon />}
                      onClick={() => onApprovalChange(row.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      Approve
                    </Button>
                  )}
                </Stack>
              </Stack>

              <Collapse in={isExpanded}>
                <Stack spacing={2.25} sx={{ bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider', p: 2.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <RuleIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Rule
                      </Typography>
                    </Stack>
                    <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="body2">{row.rule}</Typography>
                    </Paper>
                  </Stack>

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TableChartIcon fontSize="small" color="action" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Validation inputs
                      </Typography>
                    </Stack>
                    <Paper variant="outlined" sx={{ bgcolor: 'background.paper', borderRadius: 1, overflow: 'hidden' }}>
                      {row.evidence.map((evidence, index) => (
                        <Box key={evidence.label}>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            sx={{ px: 2, py: 1.25 }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ flex: '0 0 240px', fontWeight: 500 }}
                            >
                              {evidence.label}
                            </Typography>
                            <TextField
                              error={evidence.error}
                              onChange={(event) => onInputOverrideChange(row.id, evidence.label, event.target.value)}
                              size="small"
                              value={inputOverrides[row.id]?.[evidence.label] ?? getInputValue(evidence)}
                              InputProps={{
                                readOnly: !evidence.canOverride || evidence.error,
                                sx: evidence.error ? { color: 'error.main' } : undefined,
                              }}
                              inputProps={{
                                'aria-invalid': evidence.error,
                                'aria-readonly': !evidence.canOverride || evidence.error,
                              }}
                              fullWidth
                            />
                          </Stack>
                          {index < row.evidence.length - 1 && <Divider />}
                        </Box>
                      ))}
                    </Paper>
                  </Stack>
                </Stack>
              </Collapse>
            </Paper>
          );
        })
      )}
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="setup row" showAutoApproved={showAutoApproved} />
    </Stack>
  );
}

function getApprovalLabel(approved: boolean, manuallyApproved: boolean) {
  if (!approved) {
    return 'Requires approval';
  }

  return manuallyApproved ? 'Manually approved' : 'Approved';
}

function getInputValue(evidence: SetupChecklistRow['evidence'][number]) {
  return evidence.canOverride && evidence.value === '-' ? '' : evidence.value;
}
